import type { PlanProjection, PlanProjectionGroup } from '@developerz/domain';
import { createMemo, For, type JSX } from 'solid-js';
import { t, tPlural } from '../i18n';
import { Panel } from './Panel';
import { PlanSliceRow, type SliceDependency } from './PlanSliceRow';

// The epic read-model (#511): a header (plan id + slice count) over the plan's
// groups in plan order. `dependsOn` carries sibling GROUP ids, so the panel
// resolves each slice's upstreams against the projection once (group id → that
// group's PR number, or its title while no PR exists) — rows never render a
// raw group id.

export type FleetEpicPanelProps = {
  readonly projection: PlanProjection;
};

export function FleetEpicPanel(props: FleetEpicPanelProps): JSX.Element {
  const groups = (): readonly PlanProjectionGroup[] => props.projection.groups;
  // Built once per projection, not once per row (was an O(n^2) rebuild across the slice list).
  const byId = createMemo(() => new Map(groups().map((g) => [g.groupId, g])));

  const resolveDeps = (group: PlanProjectionGroup): SliceDependency[] => {
    const siblings = byId();
    return group.dependsOn.map((groupId) => {
      const upstream = siblings.get(groupId);
      return {
        groupId,
        title: upstream?.title ?? groupId,
        pullRequestNumber: upstream?.pullRequest?.number ?? null,
      };
    });
  };

  return (
    <Panel
      title={t('fleet.epic.title')}
      action={
        <span class="epic__id">
          <span>
            {t('fleet.epic.plan_label')} <code>{props.projection.planId}</code>
          </span>
          <span>{tPlural('fleet.epic.slices', groups().length, { count: groups().length })}</span>
        </span>
      }
    >
      <ul class="epic__slices">
        <For each={groups()}>
          {(group) => (
            <PlanSliceRow group={group} total={groups().length} dependsOn={resolveDeps(group)} />
          )}
        </For>
      </ul>
    </Panel>
  );
}
